import { useStrings } from '../../state/useLocale';
import { Button } from '../../ui';
import type { ChatRow } from '../../state/useChat';
import { RichText } from './CustomEmoji';

/**
 * Taking your own message back, with the message in front of you.
 *
 * It takes the panel over the way the report form does, for the reason that
 * one does: this is a deliberate stop rather than something done in passing,
 * and the menu it was opened from has closed by the time it appears. In a
 * panel at its 280px floor there is no room for a message and a confirmation
 * side by side, and a confirmation that cannot show what it is about is a
 * confirmation nobody can actually give.
 *
 * Which is the argument for it existing at all. Every other action in the
 * message menu is either reversible — a pin, a reaction, a block — or lands
 * somewhere a person will read it before anything happens, which is what a
 * report is. This is the only one that destroys something, and the only one
 * whose failure mode is a sentence that cannot be got back. A menu item that
 * deleted on the first click would be one mis-aimed press away from that, on a
 * row of six items that are all safe.
 *
 * There is no "sent" state, unlike the report. A deletion has an answer — the
 * room broadcasts the tombstone to every socket including this one — so the
 * confirmation is the message visibly becoming a blank row behind this screen,
 * which is better than a sentence saying it did. The dialog closes on the
 * press and the list says what happened.
 *
 * The tombstone is described rather than glossed over. Somebody expecting the
 * row to vanish completely should learn that it does not here, where they can
 * still change their mind, rather than afterwards from a gap they did not
 * expect and cannot remove.
 *
 * No quoted author and no tag, which the report screen does carry. That screen
 * is about somebody else and has to be unambiguous about who; this one is
 * about you, there is only one person it can be, and a card with your own name
 * and tag on it would be the panel introducing you to yourself.
 */
export interface DeleteDialogProps {
  row: ChatRow;
  onConfirm(): void;
  onClose(): void;
}

export function DeleteDialog({ row, onConfirm, onClose }: DeleteDialogProps) {
  const s = useStrings().chat;

  return (
    <div className="chat-report">
      <p className="label">{s.remove.title}</p>

      <div className="chat-report-quote">
        <span className="label">{s.remove.quoted}</span>
        {/* Either script, like every other place a message is drawn. */}
        <div className="msg-text" dir="auto">
          <RichText text={row.text} />
        </div>
      </div>

      <p className="note">{s.remove.what}</p>
      <p className="note">{s.remove.undo}</p>

      <div className="chat-report-actions">
        {/* Solid, because it is what this screen is for and a dialog whose
            own subject is the quiet button is a dialog that reads as a
            mistake. The safeguard is that the screen exists at all, not that
            the button is hard to find — and there is no red in this product
            to make it with. Cancel is the quiet one beside it. */}
        <Button
          type="button"
          variant="solid"
          title={s.menu.deleteWhy}
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          {s.remove.confirm}
        </Button>
        <Button type="button" variant="quiet" onClick={onClose}>
          {s.remove.cancel}
        </Button>
      </div>
    </div>
  );
}

export default DeleteDialog;
